import { useState, useEffect, forwardRef, useCallback, useMemo, useRef } from "react";
import { debounce } from "@tellescope/utils";
import { Input, Popover } from "@tellescope/design-system";
import { Button } from "./button";
import type { ComponentProps, FocusEventHandler, KeyboardEventHandler } from "react";

const SCIENTIFIC_NOTATION_KEYS = new Set(["e", "E", "+"]);

// Number.prototype.toString switches to exponent notation at >= 1e21 and for very small magnitudes,
// which would put an "e" into the input's value and get the response rejected downstream as
// non-numeric. toFixed also yields the exponent form past 1e21, so formatting goes through
// toLocaleString, which stays in plain decimal at every magnitude.
const toPlainNotation = (val: number): string => {
  if (!Number.isFinite(val) || !val.toString().includes("e")) return val.toString();
  return val.toLocaleString("en-US", { useGrouping: false, maximumFractionDigits: 20 });
};

export interface NumberInputPrecisionOptions {
  /** Maximum number of decimal places allowed during typing */
  maxDecimalDigitCount?: number;
  /** Fixed number of decimal places for display (overrides maxDecimalDigitCount) */
  fixedDecimalDigitCount?: number;
}

export interface NumberInputOption {
  value: number;
  label: string;
}

export interface NumberInputProps extends Omit<ComponentProps<typeof Input>, "type" | "step" | "onChange"> {
  /** Callback fired when the numeric value changes (only called with finalized values) */
  onChange?: (value: number | undefined) => void;
  /** The current numeric value (controlled) */
  value?: number;
  /** The default numeric value (uncontrolled) */
  defaultValue?: number;
  /** Precision configuration for decimal handling */
  precisionOptions?: NumberInputPrecisionOptions;
  /** Hide browser number input increment/decrement controls */
  hideControls?: boolean;
  /** Step value for number input */
  step?: number;
  /** Allow programmatic value updates even when input is focused (default: false) */
  allowUpdateWhileFocused?: boolean;
  /** Optional pre-defined options displayed in a popover when the input is focused */
  options?: NumberInputOption[];
  /**
   * Permit exponent notation (`1e5`). Defaults to `false`, refusing the `e`/`E`/`+` keystrokes.
   *
   * Left on, the browser accepts those characters as valid number-literal parts while reporting
   * `value === ""` for an incomplete exponent, so the text stays visible in the box with no numeric
   * value behind it. Only enable this where callers genuinely want exponents.
   */
  allowScientificNotation?: boolean;
}

/**
 * A flexible numeric input component with configurable precision handling.
 *
 * Features:
 * - Configurable decimal precision (max digits or fixed digits)
 * - Optional hiding of browser spin buttons
 * - Debounced solidification after 300ms of no typing (triggers onChange)
 * - Validates decimal precision during typing
 * - Manages internal display state vs external numeric value
 * - Only exposes finalized numeric values via onChange/onBlur callbacks
 * - Optional pre-defined options shown in a popover on focus
 * - Rejects exponent notation unless allowScientificNotation is set
 *
 * @param onChange - Callback fired when the value changes (receives numeric value)
 * @param onBlur - Standard blur event handler (receives original event)
 * @param value - The current numeric value for controlled usage
 * @param defaultValue - The default numeric value for uncontrolled usage
 * @param precisionOptions - Configuration for decimal precision handling
 * @param hideControls - Hide browser number input spin buttons (default: false)
 * @param step - Step value for number input (auto-calculated based on precision if not provided)
 * @param options - Pre-defined options shown in a popover when the input is focused
 * @param className - Additional CSS classes to apply
 * @param ...props - All other Input component props (except type, step, onChange)
 *
 * @example
 * ```tsx
 * // Basic usage with max 2 decimal places
 * <NumberInput
 *   precisionOptions={{ maxDecimalDigitCount: 2 }}
 *   onChange={(value) => console.log('Value:', value)}
 * />
 *
 * // Fixed 3 decimal places with hidden controls
 * <NumberInput
 *   precisionOptions={{ fixedDecimalDigitCount: 3 }}
 *   hideControls={true}
 *   value={amount}
 *   onChange={setAmount}
 * />
 *
 * // With pre-defined options
 * <NumberInput
 *   value={amount}
 *   onChange={setAmount}
 *   options={[
 *     { value: 10, label: "$10" },
 *     { value: 25, label: "$25" },
 *     { value: 50, label: "$50" },
 *   ]}
 * />
 * ```
 */
export const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(
  (
    {
      onChange,
      onFocus,
      onBlur,
      onKeyDown,
      value,
      defaultValue,
      precisionOptions,
      hideControls = false,
      step,
      allowUpdateWhileFocused = false,
      options,
      allowScientificNotation = false,
      min,
      max,
      className = "",
      ...props
    },
    ref
  ) => {
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [popoverIsOpen, setPopoverIsOpen] = useState(false);

    const exceedsBounds = useCallback(
      (val: number): boolean => {
        const upper = max === undefined || max === "" ? undefined : Number(max);
        const lower = min === undefined || min === "" ? undefined : Number(min);
        if (upper !== undefined && !Number.isNaN(upper) && val > upper) return true;
        return lower !== undefined && !Number.isNaN(lower) && val < lower;
      },
      [min, max]
    );

    // Calculate step value based on precision options if not explicitly provided
    const calculatedStep = useMemo(() => {
      if (step !== undefined) return step;

      const fixedDigits = precisionOptions?.fixedDecimalDigitCount;
      const maxDigits = precisionOptions?.maxDecimalDigitCount;

      if (fixedDigits !== undefined) {
        return Math.pow(10, -fixedDigits);
      }
      if (maxDigits !== undefined) {
        return Math.pow(10, -maxDigits);
      }

      return undefined;
    }, [step, precisionOptions]);

    // maxDecimalDigitCount blocks over-precise keystrokes, but a value can still arrive by
    // paste or from a caller's state, so the emitted number is truncated (not rounded) to the
    // same precision formatValue displays. fixedDecimalDigitCount is display padding only and
    // deliberately left alone.
    const applyPrecision = useCallback(
      (val: number): number => {
        const maxDigits = precisionOptions?.maxDecimalDigitCount;
        if (maxDigits === undefined || precisionOptions?.fixedDecimalDigitCount !== undefined) return val;
        const factor = Math.pow(10, maxDigits);
        return Math.trunc(val * factor) / factor;
      },
      [precisionOptions]
    );

    // Format initial value based on precision options
    const formatValue = useCallback(
      (val: number): string => {
        const fixedDigits = precisionOptions?.fixedDecimalDigitCount;
        const maxDigits = precisionOptions?.maxDecimalDigitCount;

        if (fixedDigits !== undefined) {
          return val.toFixed(fixedDigits);
        }

        if (maxDigits !== undefined) {
          // For maxDecimalDigitCount, truncate to the specified number of decimals
          const stringValue = toPlainNotation(val);
          const parts = stringValue.split(".");
          const integerPart = parts[0] ?? "";
          const decimalPart = parts[1];

          if (decimalPart && decimalPart.length > maxDigits) {
            return maxDigits === 0 ? integerPart : `${integerPart}.${decimalPart.slice(0, maxDigits)}`;
          }
        }

        return toPlainNotation(val);
      },
      [precisionOptions]
    );

    const [displayValue, setDisplayValue] = useState<string>(() => {
      if (value !== undefined) {
        return formatValue(value);
      }
      if (defaultValue !== undefined) {
        return formatValue(defaultValue);
      }
      return "";
    });

    // Sync display value when controlled value changes (but not while input is focused)
    useEffect(() => {
      if (value !== undefined) {
        const shouldUpdate = allowUpdateWhileFocused || inputRef.current !== document.activeElement;
        if (shouldUpdate) {
          setDisplayValue(formatValue(value));
        }
      }
    }, [value, formatValue, allowUpdateWhileFocused]);

    // Create debounced solidification function
    const debouncedSolidify = useMemo(
      () =>
        debounce(() => {
          const currentValue = inputRef.current?.value ?? "";
          const numericValue = currentValue === "" ? undefined : parseFloat(currentValue);

          // Call onChange with the finalized numeric value
          if (onChange) {
            onChange(
              numericValue === undefined || Number.isNaN(numericValue) ? undefined : applyPrecision(numericValue)
            );
          }
        }, 300),
      [onChange, applyPrecision]
    );

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;

      // Validate decimal places based on precision options
      const decimalCount = inputValue.split(".")[1]?.length ?? 0;
      const maxDecimals = precisionOptions?.fixedDecimalDigitCount ?? precisionOptions?.maxDecimalDigitCount;

      if (maxDecimals !== undefined && decimalCount > maxDecimals) {
        return;
      }

      // Refusing the keystroke that would breach the bound keeps the field from ever holding a
      // value the caller must later reject. A lone "-" is let through so a negative can be typed.
      if (inputValue !== "" && inputValue !== "-") {
        const parsed = Number(inputValue);
        if (!Number.isNaN(parsed) && exceedsBounds(parsed)) {
          return;
        }
      }

      // The browser reports "" for a value it holds but cannot parse (e.g. "1e", "-"). Writing the
      // element's value clears that buffer, so the box can't show text with no numeric value behind it.
      if (!allowScientificNotation && inputValue === "" && e.target.validity.badInput) {
        e.target.value = "";
      }

      setDisplayValue(inputValue);

      // Trigger debounced solidification
      debouncedSolidify();
    };

    const handleInputKeyDown = useCallback<KeyboardEventHandler<HTMLInputElement>>(
      (e) => {
        if (!allowScientificNotation && SCIENTIFIC_NOTATION_KEYS.has(e.key)) {
          e.preventDefault();
          return;
        }
        if (onKeyDown) {
          onKeyDown(e);
        }
      },
      [allowScientificNotation, onKeyDown]
    );

    const handleInputFocus = useCallback<FocusEventHandler<HTMLInputElement>>(
      (e) => {
        if (options?.length) {
          setPopoverIsOpen(true);
        }
        if (onFocus) {
          onFocus(e);
        }
      },
      [onFocus, options]
    );

    const handleInputBlur = useCallback<FocusEventHandler<HTMLInputElement>>(
      (...args) => {
        const parsed = displayValue ? parseFloat(displayValue) : undefined;
        const numericValue = parsed === undefined || Number.isNaN(parsed) ? undefined : applyPrecision(parsed);

        setDisplayValue(numericValue === undefined ? "" : formatValue(numericValue));

        // Only call external onChange/onBlur with the finalized numeric value
        if (onChange) {
          onChange(numericValue);
        }
        if (onBlur) {
          onBlur(...args);
        }
      },
      [onBlur, onChange, displayValue, formatValue, applyPrecision]
    );

    const handleOptionClick = useCallback(
      (option: NumberInputOption) => {
        setDisplayValue(formatValue(option.value));
        if (onChange) {
          onChange(option.value);
        }
        setPopoverIsOpen(false);
        inputRef.current?.blur();
      },
      [formatValue, onChange]
    );

    const combinedClassName = useMemo(() => {
      const hideControlsClass = hideControls
        ? "[&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]"
        : "";
      return `${hideControlsClass} ${className}`.trim();
    }, [hideControls, className]);

    const inputElement = (
      <Input
        {...props}
        ref={(node) => {
          inputRef.current = node;
          if (typeof ref === "function") {
            ref(node);
          } else if (ref) {
            ref.current = node;
          }
        }}
        type="number"
        min={min}
        max={max}
        step={calculatedStep}
        className={combinedClassName}
        value={displayValue}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        onKeyDown={handleInputKeyDown}
      />
    );

    if (!options?.length) {
      return inputElement;
    }

    return (
      <Popover open={popoverIsOpen} onOpenChange={setPopoverIsOpen}>
        <Popover.Anchor asChild>{inputElement}</Popover.Anchor>
        <Popover.Content
          className="flex flex-col gap-0 p-1 w-(--radix-popover-trigger-width)"
          align="start"
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
          onInteractOutside={(e) => {
            if (e.target === inputRef.current) {
              e.preventDefault();
            }
          }}
        >
          {options.map((option) => (
            <Button
              key={`${option.value}-${option.label}`}
              onClick={() => handleOptionClick(option)}
              variant="ghost"
              className="text-sm font-light p-1 justify-start w-full h-fit"
            >
              {option.label}
            </Button>
          ))}
        </Popover.Content>
      </Popover>
    );
  }
);

NumberInput.displayName = "NumberInput";
